import {
  DEFAULT_PATTERN_TYPE,
  MAX_COLORS,
  MAX_COLUMNS,
  MIN_COLORS,
  MIN_COLUMNS,
} from '@miyuki/domain';
import type { PatternType } from '@miyuki/types';
import { Button, Input, Label, Sheet, SheetContent, SheetHeader, SheetTitle } from '@miyuki/ui';
import * as React from 'react';

import { PatternTypePicker } from './pattern-type-picker';

import { t } from '@/lib/i18n';

/**
 * Ajustes de un diseño nuevo: tejido, ancho y cantidad de colores.
 *
 * Vive en un panel y no en la pantalla porque son ajustes que no se usan
 * mientras se dibuja (RF-49). Los valores fuera de rango se rechazan con el
 * motivo escrito, no deshabilitando el botón sin explicación.
 */
export interface NewDesignSheetProps {
  readonly open: boolean;
  readonly onCreate: (columns: number, colorCount: number, patternType: PatternType) => void;
  readonly onClose: () => void;
}

const DEFAULT_COLUMNS = 11;
const DEFAULT_COLORS = 4;

function parseCount(value: string): number {
  return Number.parseInt(value, 10);
}

export function NewDesignSheet({ open, onCreate, onClose }: NewDesignSheetProps) {
  const [columns, setColumns] = React.useState(String(DEFAULT_COLUMNS));
  const [colorCount, setColorCount] = React.useState(String(DEFAULT_COLORS));
  // Quien no elige tejido quiere una cuadrícula recta (RF-04).
  const [patternType, setPatternType] = React.useState<PatternType>(DEFAULT_PATTERN_TYPE);

  const parsedColumns = parseCount(columns);
  const parsedColors = parseCount(colorCount);

  const columnsError =
    Number.isInteger(parsedColumns) && parsedColumns >= MIN_COLUMNS && parsedColumns <= MAX_COLUMNS
      ? null
      : t('columnsOutOfRange');

  const colorsError =
    Number.isInteger(parsedColors) && parsedColors >= MIN_COLORS && parsedColors <= MAX_COLORS
      ? null
      : t('colorsOutOfRange');

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <SheetContent side="bottom" className="pb-8">
        <SheetHeader>
          <SheetTitle>{t('newDesign')}</SheetTitle>
        </SheetHeader>

        <div className="flex flex-col gap-4 px-4 pb-2">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="new-pattern-type">{t('patternType')}</Label>
            <PatternTypePicker
              id="new-pattern-type"
              value={patternType}
              onChange={setPatternType}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="new-columns">{t('columns')}</Label>
            <Input
              id="new-columns"
              type="number"
              inputMode="numeric"
              min={MIN_COLUMNS}
              max={MAX_COLUMNS}
              className="h-11"
              value={columns}
              aria-invalid={columnsError !== null}
              onChange={(event) => {
                setColumns(event.target.value);
              }}
            />
            {columnsError && <p className="text-destructive text-sm">{columnsError}</p>}
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="new-colors">{t('colors')}</Label>
            <Input
              id="new-colors"
              type="number"
              inputMode="numeric"
              min={MIN_COLORS}
              max={MAX_COLORS}
              className="h-11"
              value={colorCount}
              aria-invalid={colorsError !== null}
              onChange={(event) => {
                setColorCount(event.target.value);
              }}
            />
            {colorsError && <p className="text-destructive text-sm">{colorsError}</p>}
          </div>

          <Button
            size="touch"
            disabled={columnsError !== null || colorsError !== null}
            onClick={() => {
              onCreate(parsedColumns, parsedColors, patternType);
            }}
          >
            {t('create')}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
